import {
  API_REFERENCE_CODE_LANGUAGE_LABELS,
  API_REFERENCE_CODE_LANGUAGE_MARKDOWN_FENCES,
  API_REFERENCE_CODE_LANGUAGE_ORDER,
  API_REFERENCE_EXAMPLES,
  type ApiReferenceExampleId,
} from './apiReferenceExamples';

const API_CODE_EXAMPLES_TAG_REGEX = /<ApiCodeExamples\s+exampleId=(?:"([^"]+)"|'([^']+)')\s*\/>/g;
const API_RESPONSE_EXAMPLES_TAG_REGEX = /<ApiResponseExamples\s+exampleId=(?:"([^"]+)"|'([^']+)')\s*\/>/g;

export interface HelpArticleCopyLabels {
  requestExamples: string;
  responseExamples: string;
  statusLabels: Partial<Record<number, string>>;
  fallbackStatusLabel: string;
}

function resolveExampleId(value: string): ApiReferenceExampleId | null {
  if (Object.prototype.hasOwnProperty.call(API_REFERENCE_EXAMPLES, value)) {
    return value as ApiReferenceExampleId;
  }
  return null;
}

function renderFencedCode(language: string, code: string): string {
  return [`\`\`\`${language}`, code, '```'].join('\n');
}

function renderApiCodeExamplesMarkdown(exampleId: ApiReferenceExampleId, labels: HelpArticleCopyLabels): string {
  const example = API_REFERENCE_EXAMPLES[exampleId];
  const parts = [
    `## ${labels.requestExamples}`,
    '',
    `**${example.method}** \`${example.fullUrl}\``,
  ];

  for (const language of API_REFERENCE_CODE_LANGUAGE_ORDER) {
    const snippet = example.examples[language];
    if (!snippet) {
      continue;
    }

    parts.push(
      '',
      `### ${API_REFERENCE_CODE_LANGUAGE_LABELS[language]}`,
      '',
      renderFencedCode(API_REFERENCE_CODE_LANGUAGE_MARKDOWN_FENCES[language], snippet),
    );
  }

  return parts.join('\n');
}

function getResponseStatusLabel(status: number, labels: HelpArticleCopyLabels): string {
  return labels.statusLabels[status] || labels.fallbackStatusLabel;
}

function renderApiResponseExamplesMarkdown(exampleId: ApiReferenceExampleId, labels: HelpArticleCopyLabels): string {
  const example = API_REFERENCE_EXAMPLES[exampleId];
  const parts = [`## ${labels.responseExamples}`];

  for (const response of example.responses) {
    const statusLabel = getResponseStatusLabel(response.status, labels);
    const title = statusLabel ? `${response.status} ${statusLabel}` : String(response.status);

    parts.push(
      '',
      `### ${title}`,
      '',
      renderFencedCode('json', response.body),
    );
  }

  return parts.join('\n');
}

function compactMarkdownBlankLines(markdown: string): string {
  return `${markdown.replace(/\n{3,}/g, '\n\n').trim()}\n`;
}

/**
 * Expands Help MDX shortcodes into copyable Markdown.
 *
 * The visual Help page renders API snippets from React components, but the
 * page-level copy action needs a portable text artifact. Keep unsupported
 * shortcodes intact so missing registry entries do not silently drop content.
 */
export function buildCopyableHelpArticleSource(source: string, labels: HelpArticleCopyLabels): string {
  if (!source.trim()) {
    return '';
  }

  const withRequestExamples = source.replace(
    API_CODE_EXAMPLES_TAG_REGEX,
    (fullMatch: string, doubleQuotedExampleId?: string, singleQuotedExampleId?: string) => {
      const exampleId = resolveExampleId(doubleQuotedExampleId || singleQuotedExampleId || '');
      if (!exampleId) {
        return fullMatch;
      }
      return renderApiCodeExamplesMarkdown(exampleId, labels);
    },
  );
  const withResponseExamples = withRequestExamples.replace(
    API_RESPONSE_EXAMPLES_TAG_REGEX,
    (fullMatch: string, doubleQuotedExampleId?: string, singleQuotedExampleId?: string) => {
      const exampleId = resolveExampleId(doubleQuotedExampleId || singleQuotedExampleId || '');
      if (!exampleId) {
        return fullMatch;
      }
      return renderApiResponseExamplesMarkdown(exampleId, labels);
    },
  );

  return compactMarkdownBlankLines(withResponseExamples);
}
